import { parseLocationString } from "@/lib/locationUtils";
import { formatPrice } from "@/lib/utils";

/** Sample listings shown on onboarding — illustrates real alert line format. */
export const NEAR_ME_ONBOARDING_ALERT_EXAMPLES = [
  { title: "4 Seater Dining table", price: 150_000 },
  { title: "45 Inches Samsung Smart TV", price: 350_000 },
] as const;

/**
 * Neighborhood (or LGA when neighborhood is absent) for near-me alert copy:
 * `{title} in {place} — {price}`.
 */
export function nearMeAlertLocationNeighborhood(
  location: string | null | undefined,
): string {
  const parsed = parseLocationString(location?.trim() ?? "");
  return (
    parsed.neighborhood?.trim() ||
    parsed.lga?.trim() ||
    parsed.state?.trim() ||
    "your area"
  );
}

/** Canonical near-me listing alert line: `{title} in {neighborhood} — {price}`. */
export function formatNearMeListingAlertLine(options: {
  itemTitle: string;
  itemLocationNeighborhood: string;
  itemPrice: number;
}): string {
  const title = options.itemTitle.trim() || "Listing";
  const place = options.itemLocationNeighborhood.trim() || "your area";
  return `${title} in ${place} — ${formatPrice(options.itemPrice)}`;
}

export function buildNearMeListingAlertLineFromItem(options: {
  itemTitle: string;
  itemLocation: string | null | undefined;
  itemPrice: number | null | undefined;
}): string {
  const price =
    options.itemPrice != null && Number.isFinite(options.itemPrice)
      ? options.itemPrice
      : 0;
  return formatNearMeListingAlertLine({
    itemTitle: options.itemTitle,
    itemLocationNeighborhood: nearMeAlertLocationNeighborhood(
      options.itemLocation,
    ),
    itemPrice: price,
  });
}

/** In-app inbox event for a single realtime near-me listing alert. */
export const NEAR_ME_REALTIME_EVENT_TYPE = "new_listing_near_you";

/** Secondary inbox body copy for realtime near-me — hidden in compact in-app UI. */
export const NEAR_ME_REALTIME_INBOX_HINT = "Tap to view on Sell It Off.";

export function isNearMeRealtimeNotification(
  eventType?: string | null,
): boolean {
  return eventType === NEAR_ME_REALTIME_EVENT_TYPE;
}

/** Prefer stored title (canonical alert line); ignore generic tap hint in body. */
export function resolveNearMeRealtimeAlertLine(
  title: string,
  body: string,
): string {
  const trimmedTitle = title.trim();
  if (trimmedTitle) return trimmedTitle;
  const trimmedBody = body.trim();
  if (trimmedBody && trimmedBody !== NEAR_ME_REALTIME_INBOX_HINT) {
    return trimmedBody;
  }
  return "New listing near you";
}
